import React, { useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = process.env.REACT_APP_API_URL || '/api';

function AIAnalysis({ lpId }) {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('anthropic_api_key') || '');
  const [report, setReport] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeResults, setCodeResults] = useState({});
  const [previewTarget, setPreviewTarget] = useState(null);
  const [customMeasure, setCustomMeasure] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const reportRef = useRef(null);
  const fileInputRef = useRef(null);

  const saveApiKey = (key) => {
    setApiKey(key);
    if (key) localStorage.setItem('anthropic_api_key', key);
    else localStorage.removeItem('anthropic_api_key');
  };

  const streamFetch = async (url, body, onText, onDone, onError) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Request failed');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'text') onText(data.content);
          else if (data.type === 'error') onError(data.content);
          else if (data.type === 'done') onDone();
        } catch {}
      }
    }
  };

  const analyze = useCallback(async () => {
    if (!apiKey.trim()) { setError('Anthropic APIキーを入力してください'); return; }
    setLoading(true);
    setError('');
    setReport('');
    setCodeResults({});
    setPreviewTarget(null);
    try {
      await streamFetch(
        `${API}/ai/analyze`,
        { apiKey: apiKey.trim(), lpId: lpId || null },
        (text) => setReport(prev => prev + text),
        () => {},
        (err) => setError(err),
      );
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, [apiKey, lpId]);

  const extractMeasures = (text) => {
    const measures = [];
    const seen = new Set();

    // Find section containing 具体的な対策 (ignore emoji variations)
    const sectionMatch = text.match(/##[^#\n]*具体的な対策([\s\S]*?)(?=\n##\s[^#]|$)/);
    const section = sectionMatch ? sectionMatch[1] : '';

    if (!section) return measures;

    const addMeasure = (name) => {
      // Clean up the name
      name = name
        .replace(/^[\d]+[.．）):：\s]+/, '')  // Remove leading numbers
        .replace(/^対策\d*[：:.\s]*/i, '')     // Remove 対策N:
        .replace(/\*\*/g, '')                   // Remove bold markers
        .trim();
      if (name && !seen.has(name) && name.length > 2 && name.length < 100
          && !/^(高|中|低|対策名|優先度|期待|概要|説明|効果)$/.test(name)) {
        seen.add(name);
        measures.push(name);
      }
    };

    // Pattern 1: ### headings
    const h3Matches = section.match(/###\s+.+/g);
    if (h3Matches) {
      h3Matches.forEach(h => addMeasure(h.replace(/^###\s+/, '')));
    }

    // Pattern 2: **bold** at start of line (- **name** or **name**)
    if (measures.length === 0) {
      const boldMatches = section.match(/(?:^|\n)\s*[-*]?\s*\*\*(.+?)\*\*/g);
      if (boldMatches) {
        boldMatches.forEach(m => {
          const inner = m.match(/\*\*(.+?)\*\*/);
          if (inner) addMeasure(inner[1]);
        });
      }
    }

    // Pattern 3: numbered items (1. name)
    if (measures.length === 0) {
      const numMatches = section.match(/(?:^|\n)\s*\d+[.．）)]\s*[^\n]+/g);
      if (numMatches) {
        numMatches.forEach(m => addMeasure(m.replace(/^[\s\n]*/, '')));
      }
    }

    return measures;
  };

  const generateCode = async (measureName) => {
    const key = measureName;
    setCodeResults(prev => ({ ...prev, [key]: { code: '', loading: true, error: null, previewHtml: null, afterCode: null } }));
    try {
      let code = '';
      await streamFetch(
        `${API}/ai/generate-code`,
        { apiKey: apiKey.trim(), lpId: lpId || null, measure: measureName, reportContext: report.substring(0, 3000) },
        (text) => {
          code += text;
          setCodeResults(prev => ({ ...prev, [key]: { ...prev[key], code } }));
        },
        () => {
          // Extract all ```html blocks in order
          const htmlBlocks = [];
          const blockRegex = /```html\n([\s\S]*?)```/g;
          let bm;
          while ((bm = blockRegex.exec(code)) !== null) {
            htmlBlocks.push(bm[1].trim());
          }

          // Try to find section preview (last block that starts with <!DOCTYPE or <html)
          let previewHtml = null;
          for (let i = htmlBlocks.length - 1; i >= 0; i--) {
            if (/^<!DOCTYPE|^<html/i.test(htmlBlocks[i])) {
              previewHtml = htmlBlocks[i];
              break;
            }
          }

          // Extract after code (second block typically, or first non-DOCTYPE block after first)
          const afterMatch = code.match(/### 修正後[^\n]*\n```html\n([\s\S]*?)```/);
          let afterCode = afterMatch ? afterMatch[1].trim() : null;

          // Fallback: if no preview found, build one from afterCode
          if (!previewHtml && afterCode) {
            previewHtml = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Hiragino Sans', 'Noto Sans JP', sans-serif; margin: 0; padding: 20px; background: #f8f9fa; }
</style>
</head>
<body>
${afterCode}
</body>
</html>`;
          }

          // Fallback afterCode: use second block if available
          if (!afterCode && htmlBlocks.length >= 2) {
            afterCode = htmlBlocks[1];
          }

          setCodeResults(prev => ({ ...prev, [key]: { ...prev[key], loading: false, previewHtml, afterCode } }));
        },
        (err) => {
          setCodeResults(prev => ({ ...prev, [key]: { ...prev[key], loading: false, error: err } }));
        },
      );
    } catch (err) {
      setCodeResults(prev => ({ ...prev, [key]: { ...prev[key], loading: false, error: err.message } }));
    }
  };

  // Full LP generation with all measures applied
  const [fullHtml, setFullHtml] = useState('');
  const [fullLoading, setFullLoading] = useState(false);
  const [fullError, setFullError] = useState('');
  const [showFullPreview, setShowFullPreview] = useState(false);

  const generateFullLP = async () => {
    const snippets = Object.entries(codeResults)
      .filter(([, v]) => v.afterCode)
      .map(([measure, v]) => ({ measure, code: v.afterCode }));

    if (snippets.length === 0) {
      setFullError('先に個別の対策コードを生成してください');
      return;
    }

    setFullLoading(true);
    setFullError('');
    setFullHtml('');
    setShowFullPreview(false);

    try {
      let html = '';
      await streamFetch(
        `${API}/ai/generate-full`,
        { apiKey: apiKey.trim(), lpId: lpId || null, codeSnippets: snippets },
        (text) => {
          html += text;
          setFullHtml(html);
        },
        () => {
          // Extract HTML from code block
          const match = html.match(/```html\n([\s\S]*?)```/);
          if (match) setFullHtml(match[1].trim());
          setFullLoading(false);
        },
        (err) => { setFullError(err); setFullLoading(false); },
      );
    } catch (err) {
      setFullError(err.message);
      setFullLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
  };

  // ===== Export functions =====
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const timestamp = () => {
    const d = new Date();
    return `${d.getFullYear()}${(d.getMonth()+1).toString().padStart(2,'0')}${d.getDate().toString().padStart(2,'0')}_${d.getHours().toString().padStart(2,'0')}${d.getMinutes().toString().padStart(2,'0')}`;
  };

  const buildFullMarkdown = () => {
    let md = report;
    const codeKeys = Object.keys(codeResults).filter(k => codeResults[k].code);
    if (codeKeys.length > 0) {
      md += '\n\n---\n\n# 生成コード\n\n';
      codeKeys.forEach(key => {
        md += `## ${key}\n\n${codeResults[key].code}\n\n`;
      });
    }
    return md;
  };

  const exportMarkdown = () => {
    downloadFile(buildFullMarkdown(), `LP分析レポート_${timestamp()}.md`, 'text/markdown;charset=utf-8');
    setShowExportMenu(false);
  };

  const exportHTML = () => {
    const md = buildFullMarkdown();
    // Convert markdown to styled HTML
    const htmlContent = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>LP ヒートマップ AI 分析レポート - ${timestamp()}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
    background: #f8f9fa; color: #1a1a2e; line-height: 1.8; padding: 0;
  }
  .container { max-width: 900px; margin: 0 auto; padding: 40px 32px; }
  .header-bar {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    color: #fff; padding: 40px 32px; text-align: center;
  }
  .header-bar h1 { font-size: 28px; margin-bottom: 8px; }
  .header-bar p { font-size: 14px; opacity: 0.8; }
  h1 { font-size: 26px; color: #1a1a2e; border-bottom: 3px solid #0f3460; padding-bottom: 12px; margin: 32px 0 20px; }
  h2 { font-size: 21px; color: #16213e; margin: 28px 0 14px; padding-left: 12px; border-left: 4px solid #e94560; }
  h3 { font-size: 17px; color: #0f3460; margin: 20px 0 10px; }
  p { margin-bottom: 12px; font-size: 15px; }
  ul, ol { padding-left: 24px; margin-bottom: 14px; }
  li { margin-bottom: 6px; font-size: 15px; }
  strong { color: #e94560; }
  table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
  th { background: #1a1a2e; color: #fff; padding: 10px 14px; text-align: left; font-weight: 600; }
  td { padding: 10px 14px; border-bottom: 1px solid #e0e0e0; }
  tr:nth-child(even) td { background: #f0f4f8; }
  code { padding: 2px 6px; background: #eef2f7; border-radius: 4px; font-family: 'Consolas', monospace; font-size: 13px; color: #e94560; }
  pre { margin: 16px 0; padding: 16px; background: #1a1a2e; border-radius: 8px; overflow-x: auto; }
  pre code { padding: 0; background: none; color: #a8d8ea; font-size: 13px; line-height: 1.6; }
  blockquote { margin: 16px 0; padding: 12px 20px; border-left: 4px solid #e94560; background: #fff5f5; border-radius: 0 8px 8px 0; }
  hr { border: none; border-top: 2px solid #e0e0e0; margin: 32px 0; }
  .footer { text-align: center; padding: 32px; color: #888; font-size: 13px; border-top: 1px solid #e0e0e0; margin-top: 40px; }
  @media print {
    body { padding: 0; background: #fff; }
    .container { padding: 20px; }
    pre { white-space: pre-wrap; word-wrap: break-word; }
  }
</style>
</head>
<body>
<div class="header-bar">
  <h1>LP Heatmap AI Analyzer</h1>
  <p>分析レポート | ${new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
</div>
<div class="container" id="report-content"></div>
<div class="footer">Generated by LP Heatmap AI Analyzer</div>
<script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"><\/script>
<script>
  const md = ${JSON.stringify(md)};
  document.getElementById('report-content').innerHTML = marked.parse(md);
<\/script>
</body>
</html>`;
    downloadFile(htmlContent, `LP分析レポート_${timestamp()}.html`, 'text/html;charset=utf-8');
    setShowExportMenu(false);
  };

  const exportJSON = () => {
    const data = {
      version: 1,
      exportedAt: new Date().toISOString(),
      lpId: lpId || null,
      report,
      codeResults: Object.fromEntries(
        Object.entries(codeResults).filter(([, v]) => v.code).map(([k, v]) => [k, { code: v.code, previewHtml: v.previewHtml, afterCode: v.afterCode }])
      ),
    };
    downloadFile(JSON.stringify(data, null, 2), `LP分析レポート_${timestamp()}.json`, 'application/json;charset=utf-8');
    setShowExportMenu(false);
  };

  const importJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.report) {
          setReport(data.report);
          if (data.codeResults) {
            const restored = {};
            Object.entries(data.codeResults).forEach(([k, v]) => {
              restored[k] = { code: v.code, loading: false, error: null, previewHtml: v.previewHtml || null, afterCode: v.afterCode || null };
            });
            setCodeResults(restored);
          }
          setError('');
        } else {
          setError('無効なレポートファイルです');
        }
      } catch {
        setError('JSONファイルの読み込みに失敗しました');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const measures = report ? extractMeasures(report) : [];

  return (
    <div>
      {/* API Key & Actions */}
      <div className="ai-config">
        <div className="ai-config-row">
          <label className="ai-label">Anthropic API Key:</label>
          <input
            type="password"
            value={apiKey}
            onChange={e => saveApiKey(e.target.value)}
            placeholder="sk-ant-..."
            className="ai-input"
          />
          <button className="ai-analyze-btn" onClick={analyze} disabled={loading}>
            {loading ? (<><span className="ai-spinner" />AI分析中...</>) : 'AI分析を実行'}
          </button>

          {/* Export / Import buttons */}
          {report && (
            <div className="export-group">
              <button className="export-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
                保存・出力
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button className="export-menu-item" onClick={exportHTML}>
                    <span className="export-icon">🌐</span>
                    HTMLレポート
                    <span className="export-desc">印刷対応・スタイル付き</span>
                  </button>
                  <button className="export-menu-item" onClick={exportMarkdown}>
                    <span className="export-icon">📝</span>
                    Markdown
                    <span className="export-desc">テキスト形式・編集可能</span>
                  </button>
                  <button className="export-menu-item" onClick={exportJSON}>
                    <span className="export-icon">💾</span>
                    JSON保存
                    <span className="export-desc">後で再読み込み可能</span>
                  </button>
                </div>
              )}
            </div>
          )}
          <label className="import-btn">
            読込
            <input ref={fileInputRef} type="file" accept=".json" onChange={importJSON} style={{ display: 'none' }} />
          </label>
        </div>
        {error && <div className="ai-error">{error}</div>}
      </div>

      {/* Report */}
      {report ? (
        <div>
          <div className="ai-report" ref={reportRef}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
          </div>

          {/* Code Generation Section */}
          <div className="code-gen-section">
            <h2 className="code-gen-title">コード生成</h2>
            <p className="code-gen-desc">各対策の実装コードをAIで生成します</p>

            {measures.length > 0 && (
              <div className="code-gen-list">
                {measures.map((measure, i) => (
                  <CodeGenItem
                    key={i}
                    measure={measure}
                    result={codeResults[measure]}
                    previewTarget={previewTarget}
                    onGenerate={generateCode}
                    onPreview={(m) => setPreviewTarget(previewTarget === m ? null : m)}
                    onCopy={copyCode}
                  />
                ))}
              </div>
            )}

            <div className="code-gen-custom">
              <p className="code-gen-custom-label">
                {measures.length > 0 ? '他の対策のコードも生成:' : '対策を入力してコードを生成:'}
              </p>
              <div className="code-gen-custom-row">
                <input
                  type="text"
                  value={customMeasure}
                  onChange={e => setCustomMeasure(e.target.value)}
                  placeholder="例: CTAボタンを大きくして目立たせる"
                  className="code-gen-custom-input"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && customMeasure.trim()) {
                      generateCode(customMeasure.trim());
                      setCustomMeasure('');
                    }
                  }}
                />
                <button
                  className="code-gen-btn"
                  onClick={() => {
                    if (customMeasure.trim()) {
                      generateCode(customMeasure.trim());
                      setCustomMeasure('');
                    }
                  }}
                  disabled={!customMeasure.trim()}
                >
                  コード生成
                </button>
              </div>
              {Object.keys(codeResults).filter(k => !measures.includes(k)).map((key, i) => (
                <div key={`custom-${i}`} style={{ marginTop: 12 }}>
                  <CodeGenItem
                    measure={key}
                    result={codeResults[key]}
                    previewTarget={previewTarget}
                    onGenerate={generateCode}
                    onPreview={(m) => setPreviewTarget(previewTarget === m ? null : m)}
                    onCopy={copyCode}
                  />
                </div>
              ))}
            </div>

            {/* Full LP generation */}
            <div className="full-gen-section">
              <div className="full-gen-header">
                <div>
                  <h3 className="full-gen-title">全対策を統合したLP生成</h3>
                  <p className="full-gen-desc">
                    生成済みの全コードをまとめて適用した完全なHTMLを生成します
                    {Object.values(codeResults).filter(v => v.afterCode).length > 0 && (
                      <span className="full-gen-count">
                        （{Object.values(codeResults).filter(v => v.afterCode).length}件の対策を適用）
                      </span>
                    )}
                  </p>
                </div>
                <button
                  className="full-gen-btn"
                  onClick={generateFullLP}
                  disabled={fullLoading || Object.values(codeResults).filter(v => v.afterCode).length === 0}
                >
                  {fullLoading ? (<><span className="ai-spinner" /> 統合HTML生成中...</>) : '統合HTML生成'}
                </button>
              </div>
              {fullError && <div className="ai-error" style={{ marginTop: 8 }}>{fullError}</div>}
              {fullHtml && !fullLoading && (
                <div className="full-gen-result">
                  <div className="full-gen-toolbar">
                    <button
                      className={`code-gen-btn preview ${showFullPreview ? 'active' : ''}`}
                      onClick={() => setShowFullPreview(!showFullPreview)}
                    >
                      {showFullPreview ? 'プレビューを閉じる' : 'プレビュー'}
                    </button>
                    <button className="code-gen-btn regen" onClick={() => copyCode(fullHtml)}>
                      HTMLをコピー
                    </button>
                    <button className="code-gen-btn regen" onClick={() => {
                      downloadFile(fullHtml, `LP_改善版_${timestamp()}.html`, 'text/html;charset=utf-8');
                    }}>
                      HTMLダウンロード
                    </button>
                  </div>
                  {showFullPreview && (
                    <div className="code-preview-container" style={{ marginTop: 12 }}>
                      <div className="code-preview-toolbar">
                        <span>全対策適用済みLP プレビュー</span>
                      </div>
                      <iframe
                        title="Full LP Preview"
                        srcDoc={fullHtml}
                        className="code-preview-iframe"
                        style={{ minHeight: 700 }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : !loading ? (
        <div className="empty-state">
          <div className="icon">🤖</div>
          <h3>AI分析</h3>
          <p>ヒートマップデータをAIが分析し、改善戦略・具体的な対策を自動生成します</p>
          <div className="ai-features">
            <div className="ai-feature-item"><span className="ai-feature-icon">📊</span><span>全体評価スコア（100点満点）</span></div>
            <div className="ai-feature-item"><span className="ai-feature-icon">🔍</span><span>主要課題トップ5（データ根拠付き）</span></div>
            <div className="ai-feature-item"><span className="ai-feature-icon">🎯</span><span>改善戦略（優先順位付き3施策）</span></div>
            <div className="ai-feature-item"><span className="ai-feature-icon">🛠️</span><span>コード生成ボタンで即実装</span></div>
            <div className="ai-feature-item"><span className="ai-feature-icon">👁️</span><span>プレビューモードで即確認</span></div>
            <div className="ai-feature-item"><span className="ai-feature-icon">📥</span><span>レポート保存・ダウンロード</span></div>
          </div>
        </div>
      ) : (
        <div className="ai-loading-state">
          <div className="ai-loading-animation"><div className="ai-pulse" /></div>
          <p>ヒートマップデータを分析中...</p>
          <p className="ai-loading-sub">クリック座標・スクロール深度・マウス移動パターンを統合解析しています</p>
          {report && (
            <div className="ai-report ai-report-streaming">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CodeGenItem({ measure, result, previewTarget, onGenerate, onPreview, onCopy }) {
  return (
    <div className="code-gen-item">
      <div className="code-gen-header">
        <span className="code-gen-name">{measure}</span>
        <div className="code-gen-actions">
          {!result || (!result.loading && !result.code) ? (
            <button className="code-gen-btn" onClick={() => onGenerate(measure)}>コード生成</button>
          ) : result.loading ? (
            <button className="code-gen-btn" disabled><span className="ai-spinner" /> 生成中...</button>
          ) : (
            <>
              <button className="code-gen-btn regen" onClick={() => onGenerate(measure)}>再生成</button>
              {result.previewHtml && (
                <button
                  className={`code-gen-btn preview ${previewTarget === measure ? 'active' : ''}`}
                  onClick={() => onPreview(measure)}
                >
                  {previewTarget === measure ? 'プレビューを閉じる' : 'プレビュー'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {result && result.error && <div className="ai-error" style={{ marginTop: 8 }}>{result.error}</div>}
      {result && result.code && (
        <div className="code-gen-result">
          <div className="code-gen-result-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.code}</ReactMarkdown>
          </div>
        </div>
      )}
      {previewTarget === measure && result && result.previewHtml && (
        <div className="code-preview-container">
          <div className="code-preview-toolbar">
            <span>プレビュー</span>
            <button className="code-preview-copy" onClick={() => onCopy(result.previewHtml)}>HTMLをコピー</button>
          </div>
          <iframe title={`Preview: ${measure}`} srcDoc={result.previewHtml} className="code-preview-iframe" />
        </div>
      )}
    </div>
  );
}

export default AIAnalysis;
